import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import DashboardNav from "../components/DashboardNav";
import CampaignCard from "../components/CampaignCard";
import {
  Shield,
  Gamepad2,
  Users,
  UserCheck,
  Building2,
  Eye,
  AlertCircle,
  CheckCircle2,
  Clock,
  Pause,
  BarChart3,
  ArrowUpDown,
} from "lucide-react";

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending Review" },
  { id: "active", label: "Active" },
  { id: "paused", label: "Paused" },
];

function AdminDashboardPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [view, setView] = useState("campaigns"); // "campaigns" | "influencers" | "publishers"
  const [influencerSort, setInfluencerSort] = useState("newest"); // "newest" | "oldest"
  const [influencerFilter, setInfluencerFilter] = useState("all"); // "all" | "approved" | "pending"

  useEffect(() => {
    async function fetchCampaigns() {
      try {
        const snap = await getDocs(
          query(collection(db, "campaigns"), orderBy("createdAt", "desc"))
        );
        setCampaigns(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to fetch campaigns:", err);
      }
      setLoadingCampaigns(false);
    }

    async function fetchUsers() {
      try {
        const snap = await getDocs(collection(db, "users"));
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
      setLoadingUsers(false);
    }

    fetchCampaigns();
    fetchUsers();
  }, []);

  const influencers = users
    .filter((u) => u.role === "influencer")
    .filter((u) => {
      if (influencerFilter === "approved") return u.isApprovedByAdmin === true;
      if (influencerFilter === "pending") return !u.isApprovedByAdmin;
      return true;
    })
    .sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return influencerSort === "newest" ? dateB - dateA : dateA - dateB;
    });
  const allInfluencers = users.filter((u) => u.role === "influencer");
  const approvedInfluencerCount = allInfluencers.filter((u) => u.isApprovedByAdmin === true).length;
  const pendingInfluencerCount = allInfluencers.filter((u) => !u.isApprovedByAdmin).length;
  const publishers = users.filter((u) => u.role === "publisher");

  const filteredCampaigns =
    activeTab === "all"
      ? campaigns
      : campaigns.filter((c) => c.status === activeTab);

  const pendingCount = campaigns.filter((c) => c.status === "pending").length;
  const activeCount = campaigns.filter((c) => c.status === "active").length;
  const pausedCount = campaigns.filter((c) => c.status === "paused").length;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-7 h-7 text-brand-400" />
          <div>
            <h1 className="text-3xl font-bold">Admin Panel</h1>
            <p className="text-gray-400 text-sm">
              Manage campaigns, influencers, and publishers
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-yellow-400 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium">Pending</span>
            </div>
            <p className="text-2xl font-bold">{pendingCount}</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-green-400 mb-1">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-xs font-medium">Active</span>
            </div>
            <p className="text-2xl font-bold">{activeCount}</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-blue-400 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium">Influencers</span>
            </div>
            <p className="text-2xl font-bold">{allInfluencers.length}</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-purple-400 mb-1">
              <Building2 className="w-4 h-4" />
              <span className="text-xs font-medium">Publishers</span>
            </div>
            <p className="text-2xl font-bold">{publishers.length}</p>
          </div>
        </div>

        {/* View Switcher */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setView("campaigns")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              view === "campaigns"
                ? "bg-brand-500/10 border border-brand-500/30 text-brand-400"
                : "bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-gray-300"
            }`}
          >
            <Gamepad2 className="w-4 h-4" />
            Campaigns ({campaigns.length})
          </button>
          <button
            onClick={() => setView("influencers")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              view === "influencers"
                ? "bg-brand-500/10 border border-brand-500/30 text-brand-400"
                : "bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-gray-300"
            }`}
          >
            <UserCheck className="w-4 h-4" />
            Influencers ({influencers.length})
          </button>
          <button
            onClick={() => setView("publishers")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              view === "publishers"
                ? "bg-brand-500/10 border border-brand-500/30 text-brand-400"
                : "bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-gray-300"
            }`}
          >
            <Building2 className="w-4 h-4" />
            Publishers ({publishers.length})
          </button>
        </div>

        {/* Campaigns View */}
        {view === "campaigns" && (
          <div>
            {/* Status Tabs */}
            <div className="flex gap-2 mb-4">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                    activeTab === tab.id
                      ? "bg-brand-500/10 border-brand-500/30 text-brand-400"
                      : "bg-gray-900/50 border-gray-800 text-gray-400 hover:text-gray-300"
                  }`}
                >
                  {tab.label}
                  {tab.id === "pending" && pendingCount > 0 && (
                    <span className="ml-1.5 bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded-full text-xs">
                      {pendingCount}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {loadingCampaigns ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                <Gamepad2 className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-gray-300">
                  No {activeTab === "all" ? "" : activeTab} campaigns
                </h3>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    linkTo={`/admin/campaigns/${campaign.id}`}
                    actions={
                      <div className="flex items-center gap-2">
                        {campaign.status === "pending" && (
                          <span className="flex items-center gap-1 text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-2.5 py-1 rounded-full">
                            <AlertCircle className="w-3 h-3" />
                            Awaiting Review
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/campaigns/${campaign.id}`);
                          }}
                          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-xl transition-all font-semibold text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          Review
                        </button>
                      </div>
                    }
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Influencers View */}
        {view === "influencers" && (
          <div>
            {loadingUsers ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : allInfluencers.length === 0 ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                <Users className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-gray-300">No influencers yet</h3>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {[
                      { id: "all", label: "All", count: allInfluencers.length },
                      { id: "approved", label: "Approved", count: approvedInfluencerCount },
                      { id: "pending", label: "Pending", count: pendingInfluencerCount },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setInfluencerFilter(tab.id)}
                        className={`text-sm px-3 py-1.5 rounded-lg transition-all ${
                          influencerFilter === tab.id
                            ? "bg-brand-600/20 text-brand-400 border border-brand-500/30"
                            : "text-gray-400 hover:text-white bg-gray-900/50 border border-gray-800/50 hover:border-gray-700"
                        }`}
                      >
                        {tab.label} ({tab.count})
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setInfluencerSort(influencerSort === "newest" ? "oldest" : "newest")}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg bg-gray-900/50 border border-gray-800/50 hover:border-gray-700 transition-all"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                    {influencerSort === "newest" ? "Newest First" : "Oldest First"}
                  </button>
                </div>
                {influencers.length === 0 ? (
                  <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                    <Users className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2 text-gray-300">
                      No {influencerFilter === "approved" ? "approved" : "pending"} influencers
                    </h3>
                  </div>
                ) : (
                <div className="grid gap-3">
                {influencers.map((inf) => (
                  <div
                    key={inf.id}
                    onClick={() => navigate(`/admin/influencers/${inf.id}`)}
                    className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5 flex items-center justify-between hover:border-gray-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400 font-bold text-sm">
                        {(inf.displayName || inf.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold">{inf.displayName || "Unnamed"}</p>
                        <p className="text-sm text-gray-500">{inf.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {inf.isApprovedByAdmin ? (
                        <span className="text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-2.5 py-1 rounded-full">
                          Approved
                        </span>
                      ) : (
                        <span className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-2.5 py-1 rounded-full">
                          Not Approved
                        </span>
                      )}
                      {inf.utmId && (
                        <span className="text-xs text-gray-500 bg-gray-800 px-2.5 py-1 rounded-full">
                          UTM: {inf.utmId}
                        </span>
                      )}
                      <span className="text-xs text-gray-600">
                        Joined {inf.createdAt ? new Date(inf.createdAt).toLocaleDateString() : "N/A"}
                      </span>
                    </div>
                  </div>
                ))}
                </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Publishers View */}
        {view === "publishers" && (
          <div>
            {loadingUsers ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : publishers.length === 0 ? (
              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
                <Building2 className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-gray-300">No publishers yet</h3>
              </div>
            ) : (
              <div className="grid gap-3">
                {publishers.map((pub) => {
                  const pubCampaigns = campaigns.filter((c) => c.publisherId === pub.id);
                  return (
                    <div
                      key={pub.id}
                      onClick={() => navigate(`/admin/publishers/${pub.id}`)}
                      className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5 flex items-center justify-between hover:border-gray-700 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 font-bold text-sm">
                          {(pub.displayName || pub.email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold">{pub.displayName || "Unnamed"}</p>
                          <p className="text-sm text-gray-500">{pub.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-400 flex items-center gap-1.5">
                          <BarChart3 className="w-3.5 h-3.5" />
                          {pubCampaigns.length} campaign{pubCampaigns.length !== 1 ? "s" : ""}
                        </span>
                        <span className="text-xs text-gray-600">
                          Joined {pub.createdAt ? new Date(pub.createdAt).toLocaleDateString() : "N/A"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboardPage;
